import { api } from '../../services/api';

class AdminApiService {
  // Stats
  async getOverviewStats() {
    const response = await api.client.get('/api/admin/stats/overview');
    return response.data;
  }

  async getRegistrationStats(period = 30) {
    const response = await api.client.get('/api/admin/stats/registrations', {
      params: { period }
    });
    return response.data;
  }

  async getTokenUsageStats(period = 30, limit = 10) {
    const response = await api.client.get('/api/admin/stats/tokens', {
      params: { period, limit }
    });
    return response.data;
  }

  async getFeedbackStats() {
    const response = await api.client.get('/api/admin/stats/feedbacks');
    return response.data;
  }

  // Workspaces
  async getWorkspaces(params = {}) {
    const response = await api.client.get('/api/admin/workspaces', { params });
    return response.data;
  }

  async createWorkspace(data) {
    const response = await api.client.post('/api/admin/workspaces', data);
    return response.data;
  }

  async updateWorkspace(id, data) {
    const response = await api.client.patch(`/api/admin/workspaces/${id}`, data);
    return response.data;
  }

  async deleteWorkspace(id) {
    const response = await api.client.delete(`/api/admin/workspaces/${id}`);
    return response.data;
  }

  // A postpaid workspace's invoice month ("YYYY-MM", Kyiv time)
  async getWorkspaceReport(id, month) {
    const response = await api.client.get(`/api/admin/workspaces/${id}/report`, { params: { month } });
    return response.data;
  }

  async downloadWorkspaceReportCsv(id, month) {
    return api.client.get(`/api/admin/workspaces/${id}/report.csv`, { params: { month }, responseType: 'blob' });
  }

  // Users
  async getUsers(params = {}) {
    const response = await api.client.get('/api/admin/users', { params });
    return response.data;
  }

  async createUser(data) {
    const response = await api.client.post('/api/admin/users', data);
    return response.data;
  }

  async updateUser(id, data) {
    const response = await api.client.patch(`/api/admin/users/${id}`, data);
    return response.data;
  }

  async deleteUser(id) {
    const response = await api.client.delete(`/api/admin/users/${id}`);
    return response.data;
  }

  // Billing ledger
  async getUserLedger(userId, params = {}) {
    const response = await api.client.get(`/api/admin/users/${userId}/ledger`, { params });
    return response.data;
  }

  async creditUser(userId, { chars, reason, idempotency_key }) {
    const response = await api.client.post(`/api/admin/users/${userId}/credit`, {
      chars, reason, idempotency_key,
    });
    return response.data;
  }

  async refundTransaction(transactionId, reason) {
    const response = await api.client.post(`/api/admin/transactions/${transactionId}/refund`, { reason });
    return response.data;
  }

  // Payments (PAYG orders)
  async getPayments(params = {}) {
    const response = await api.client.get('/api/admin/payments', { params });
    return response.data;
  }

  // Shares (QA report share links)
  async getQaShares(params = {}) {
    const response = await api.client.get('/api/admin/qa-shares', { params });
    return response.data;
  }

  // Chats
  async getChats(params = {}) {
    const response = await api.client.get('/api/admin/chats', { params });
    return response.data;
  }

  async deleteChat(id) {
    const response = await api.client.delete(`/api/admin/chats/${id}`);
    return response.data;
  }

  // Documents
  async getDocuments(params = {}) {
    const response = await api.client.get('/api/admin/documents', { params });
    return response.data;
  }

  async downloadDocument(id, fileType = 'original') {
    const response = await api.client.get(`/api/admin/documents/${id}/download`, {
      params: { file_type: fileType },
      responseType: 'blob'
    });
    return response;
  }

  async deleteDocument(id) {
    const response = await api.client.delete(`/api/admin/documents/${id}`);
    return response.data;
  }

  // Blog Analytics
  async getBlogViewStats(period = 30) {
    const response = await api.client.get('/api/admin/stats/blog-views', {
      params: { period }
    });
    return response.data;
  }

  async getDailyBlogViews(slug = null, period = 30) {
    const response = await api.client.get('/api/admin/stats/blog-views/daily', {
      params: { slug, period }
    });
    return response.data;
  }

  async getCtaClickStats(period = 30) {
    const response = await api.client.get('/api/admin/stats/cta-clicks', {
      params: { period }
    });
    return response.data;
  }

  async getBlogConversions(period = 30, limit = 10) {
    const response = await api.client.get('/api/admin/stats/blog-conversions', {
      params: { period, limit }
    });
    return response.data;
  }

  // Feedbacks
  async getFeedbacks(params = {}) {
    const response = await api.client.get('/api/admin/feedbacks', { params });
    return response.data;
  }

  async updateFeedback(id, data) {
    const response = await api.client.patch(`/api/admin/feedbacks/${id}`, data);
    return response.data;
  }

  async deleteFeedback(id) {
    const response = await api.client.delete(`/api/admin/feedbacks/${id}`);
    return response.data;
  }

  // Inline translation comments
  async getTranslationComments(params = {}) {
    const response = await api.client.get('/api/admin/translation-comments', { params });
    return response.data;
  }

  // Receipt templates (admin can manage templates for any workspace)
  async getReceiptTemplates(workspaceId) {
    const response = await api.client.get(`/api/admin/workspaces/${workspaceId}/receipt-templates`);
    return response.data;
  }

  async createReceiptTemplate(workspaceId, file, name) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    const response = await api.client.post(
      `/api/admin/workspaces/${workspaceId}/receipt-templates`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  async updateReceiptTemplate(workspaceId, templateId, { name, file } = {}) {
    const formData = new FormData();
    if (name !== undefined && name !== null) formData.append('name', name);
    if (file) formData.append('file', file);
    const response = await api.client.patch(
      `/api/admin/workspaces/${workspaceId}/receipt-templates/${templateId}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  async deleteReceiptTemplate(workspaceId, templateId) {
    await api.client.delete(`/api/admin/workspaces/${workspaceId}/receipt-templates/${templateId}`);
  }
}

export const adminApi = new AdminApiService();
